import { DayIndex } from 'components/mrp/components/AppointmentForm/components/RecurrencePatternBuilder/RecurrencePatternBuilder.types';
import dayjs from 'dayjs';

export const calculateDailyPreviews = (
  interval: number,
  startDate: dayjs.Dayjs,
  endDate: dayjs.Dayjs,
  appointmentStart: dayjs.Dayjs,
  appointmentEnd: dayjs.Dayjs,
  appointmentDurationInMinutes: number
) => {
  // A positive integer (min: 1) indicating the number of days between the calendar start and the appointment startdate
  const daysSinceAppointmentStart = Math.max(
    1,
    startDate.diff(appointmentStart, 'day')
  );

  // Divide the difference by the interval size to calculate the number of days to offset.
  // I.e. for a dayCount of 40 and an interval of 3, the expected result is 14 (or 13,67 repeating ceiled).
  const offsetMultiplier = Math.ceil(daysSinceAppointmentStart / interval);

  // The first recurrence is the offsetMultiplier times the interval, added to the appointment's startDate.
  const firstRecurrence = appointmentStart.add(
    offsetMultiplier * interval,
    'days'
  );

  const recurrences: { previewStart: string; previewEnd: string }[] = [];

  if (startDate.isBefore(appointmentEnd) && endDate.isAfter(appointmentStart))
    recurrences.push({
      previewStart: appointmentStart.toISOString(),
      previewEnd: appointmentEnd.toISOString()
    });

  while (firstRecurrence.add(recurrences.length * interval, 'days') < endDate) {
    const nextRecurrence = firstRecurrence.add(
      recurrences.length * interval,
      'days'
    );
    recurrences.push({
      previewStart: nextRecurrence.toISOString(),
      previewEnd: nextRecurrence
        .add(appointmentDurationInMinutes, 'minutes')
        .toISOString()
    });
  }

  return recurrences;
};

export const calculateWeeklyPreviews = (
  interval: number,
  startDate: dayjs.Dayjs,
  endDate: dayjs.Dayjs,
  appointmentStart: dayjs.Dayjs,
  appointmentDurationInMinutes: number,
  daysSelected: DayIndex[]
) => {
  const appointmentWeek = appointmentStart.isoWeek();
  const dateWeek = startDate.isoWeek();
  const previewsInWeek = (dateWeek - appointmentWeek) % interval == 0;

  if (!previewsInWeek) {
    return [];
  }

  const patternStartDate = appointmentStart.startOf('day');
  const appointmentStartTimeMinutes = appointmentStart.diff(
    patternStartDate,
    'minutes'
  );

  const lowerThresholdDate = patternStartDate.isAfter(startDate)
    ? patternStartDate
    : startDate;

  const dayCount = endDate.diff(lowerThresholdDate, 'days');

  const recurrences = Array.from({ length: dayCount + 1 })
    .map((_, idx) =>
      lowerThresholdDate
        .add(idx, 'day')
        .add(appointmentStartTimeMinutes, 'minutes')
    )
    .filter(
      (recurrence) =>
        recurrence.isAfter(patternStartDate) &&
        daysSelected.indexOf((recurrence.isoWeekday() - 1) as DayIndex) != -1
    )
    .map((recurrence) => ({
      previewStart: recurrence.toISOString(),
      previewEnd: recurrence
        .add(appointmentDurationInMinutes, 'minutes')
        .toISOString()
    }));

  return recurrences;
};
